import { useParams, Link } from "react-router-dom";
import { useSEO, breadcrumbSchema } from "@/lib/seo";

const PAGES = {
  privacy: {
    title: "Privacy Policy",
    description: "How Mashara Finance collects, uses and protects your account, business and usage data. Payments handled securely by Razorpay.",
    body: [
      ["What we collect", "Account details (name, email, mobile, organization), business data you enter into the platform, and usage analytics (pages visited, events, UTM campaign parameters) used to improve the product and attribute marketing."],
      ["How we use it", "To provide the service, activate trials and subscriptions, communicate about your account, and measure marketing effectiveness. We do not sell your data."],
      ["Payments", "Payments are processed by Razorpay. We never see or store your card or UPI credentials."],
      ["Your data", "Your business data remains yours. Reports and records can be exported. Contact us to delete your account and associated data."],
      ["Contact", "For privacy questions, reach us on WhatsApp at +91 91620 55572."],
    ],
  },
  terms: {
    title: "Terms of Service",
    description: "Terms governing use of the Mashara Finance platform: free trial, subscriptions, plan-based access, acceptable use and cancellation.",
    body: [
      ["The service", "Mashara Finance provides a cloud business management platform covering finance, HR, attendance, payroll, expenses, invoicing and reporting, subject to your subscription plan."],
      ["Free trial", "New accounts receive a 10-day free trial of the full platform, activated by a ₹1 verification payment. No subscription is charged during the trial period."],
      ["Subscriptions", "Paid plans are billed monthly or annually in advance. Plan-based module access is enforced by the platform."],
      ["Acceptable use", "You agree to use the platform lawfully and to keep your account credentials secure."],
      ["Cancellation", "You can cancel anytime. Access continues until the end of the paid period."],
    ],
  },
  refund: {
    title: "Refund & Cancellation Policy",
    description: "Mashara Finance refund and cancellation policy: ₹1 trial verification, cancelling subscriptions, refund eligibility and plan changes.",
    body: [
      ["Trial verification", "The ₹1 trial verification payment is non-refundable as it verifies your payment method and business identity."],
      ["Cancellation", "You may cancel your subscription anytime from your dashboard or by contacting support. Cancellation takes effect at the end of the current billing period."],
      ["Refunds", "If you believe you were charged in error, contact us within 7 days of the charge on WhatsApp (+91 91620 55572) and we will review and refund eligible charges promptly."],
      ["Plan changes", "Upgrades and downgrades take effect on your next billing cycle."],
    ],
  },
};

export default function Legal() {
  const { slug } = useParams();
  const key = PAGES[slug] ? slug : "privacy";
  const page = PAGES[key];
  useSEO({
    title: `${page.title} — Mashara Finance`,
    description: page.description,
    path: `/legal/${slug}`,
    canonicalPath: `/legal/${key}`,
    jsonLd: breadcrumbSchema([{ name: "Home", path: "/" }, { name: page.title, path: `/legal/${key}` }]),
  });

  return (
    <div data-testid={`legal-${slug}`} className="py-16 sm:py-24">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="font-display text-4xl font-bold tracking-tight text-slate-900 mb-10">{page.title}</h1>
        <div className="space-y-8">
          {page.body.map(([h, p]) => (
            <div key={h}>
              <h2 className="font-display font-bold text-lg text-slate-900 mb-2">{h}</h2>
              <p className="text-slate-600 leading-relaxed text-sm sm:text-base">{p}</p>
            </div>
          ))}
        </div>
        <p className="mt-12 text-sm text-slate-500">Last updated: June 2026 · Questions? <Link to="/demo" className="text-emerald-700 font-medium">Contact us</Link></p>
      </div>
    </div>
  );
}
